/**
 * SuperUser-only encrypted backup & restore page.
 *
 * Three cards:
 *   1. ScheduleCard — the server-side automatic-backup programme (GET/PUT
 *      /admin/backup/schedule): period, daily hour (UTC), server/Drive
 *      retention, plus read-only indicators for passphrase/Drive config.
 *   2. Manual backup — passphrase (+ confirm) then download the `.rmtkbk`.
 *   3. Restore — file + passphrase, guarded by a destructive-action dialog.
 *
 * The passphrase never leaves these forms except as the `X-Backup-Passphrase`
 * request header; it is required to both create and later decrypt an archive.
 */

import { useEffect, useRef, useState } from "react";
import { Title, useNotify, useTranslate } from "react-admin";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import RestoreIcon from "@mui/icons-material/Restore";
import { useIsSuperUser } from "@/utils/permissions";
import {
  MIN_PASSPHRASE,
  downloadBackup,
  getSchedule,
  restoreBackup,
  saveSchedule,
} from "../services/backupService";
import type { BackupSchedule } from "../types/backup";

const fmtUtc = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return (
    d.toLocaleString("es-ES", {
      timeZone: "UTC",
      dateStyle: "medium",
      timeStyle: "short",
    }) + " UTC"
  );
};

/**
 * Automatic-backup schedule: period, daily hour (UTC), server/Drive copies kept
 * and whether it is enabled. Read/written against GET/PUT
 * /admin/backup/schedule; the server applies changes without restarting.
 */
const ScheduleCard = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [s, setS] = useState<BackupSchedule | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSchedule()
      .then(setS)
      .catch((e) =>
        setLoadErr(
          e instanceof Error ? e.message : translate("resources.backup.schedule.loadError"),
        ),
      );
  }, [translate]);

  const set = (patch: Partial<BackupSchedule>) =>
    setS((prev) => (prev ? { ...prev, ...patch } : prev));
  const num = (v: string) => (v === "" ? 0 : Number(v));

  const save = async () => {
    if (!s) return;
    setSaving(true);
    try {
      const saved = await saveSchedule({
        enabled: s.enabled,
        intervalHours: s.intervalHours,
        dailyHourUtc: s.dailyHourUtc,
        retention: s.retention,
        driveRetention: s.driveRetention,
      });
      setS(saved);
      notify("resources.backup.schedule.saved", { type: "success" });
    } catch (e) {
      notify(
        e instanceof Error ? e.message : translate("resources.backup.schedule.saveError"),
        { type: "error" },
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mt: 2, maxWidth: 720 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {translate("resources.backup.schedule.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {translate(
            s?.driveConfigured
              ? "resources.backup.schedule.introDrive"
              : "resources.backup.schedule.intro",
          )}
        </Typography>

        {loadErr && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadErr}
          </Alert>
        )}
        {s && !s.passphraseConfigured && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {translate("resources.backup.schedule.noPassphrase")}
          </Alert>
        )}

        {s && (
          <Stack spacing={2} sx={{ maxWidth: 520 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={s.enabled}
                  onChange={(e) => set({ enabled: e.target.checked })}
                />
              }
              label={translate("resources.backup.schedule.enable")}
            />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 2,
              }}
            >
              <TextField
                type="number"
                label={translate("resources.backup.schedule.intervalHours")}
                value={s.intervalHours}
                onChange={(e) => set({ intervalHours: num(e.target.value) })}
                helperText={translate("resources.backup.schedule.intervalHoursHint")}
                inputProps={{ min: 1, step: 1 }}
              />
              <TextField
                type="number"
                label={translate("resources.backup.schedule.dailyHourUtc")}
                value={s.dailyHourUtc}
                onChange={(e) => set({ dailyHourUtc: num(e.target.value) })}
                helperText={translate("resources.backup.schedule.dailyHourUtcHint")}
                inputProps={{ min: 0, max: 23, step: 1 }}
              />
              <TextField
                type="number"
                label={translate("resources.backup.schedule.retention")}
                value={s.retention}
                onChange={(e) => set({ retention: num(e.target.value) })}
                helperText={translate("resources.backup.schedule.retentionHint")}
                inputProps={{ min: 0, step: 1 }}
              />
              <TextField
                type="number"
                label={translate("resources.backup.schedule.driveRetention")}
                value={s.driveRetention}
                disabled={!s.driveConfigured}
                onChange={(e) => set({ driveRetention: num(e.target.value) })}
                helperText={translate(
                  s.driveConfigured
                    ? "resources.backup.schedule.driveRetentionHint"
                    : "resources.backup.schedule.driveDisabledHint",
                )}
                inputProps={{ min: 0, step: 1 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {translate("resources.backup.schedule.lastRun")}:{" "}
              <b>{fmtUtc(s.lastRunUtc)}</b> ·{" "}
              {translate("resources.backup.schedule.nextRun")}:{" "}
              <b>{s.enabled ? fmtUtc(s.nextRunUtc) : "—"}</b>
            </Typography>
            <Box>
              <Button variant="contained" disabled={saving} onClick={save}>
                {saving
                  ? translate("resources.backup.schedule.saving")
                  : translate("resources.backup.schedule.save")}
              </Button>
            </Box>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export const BackupPage = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const isSuperUser = useIsSuperUser();

  // Backup state
  const [backupPass, setBackupPass] = useState("");
  const [backupPass2, setBackupPass2] = useState("");
  const [downloading, setDownloading] = useState(false);

  // Restore state
  const fileRef = useRef<HTMLInputElement>(null);
  const [restorePass, setRestorePass] = useState("");
  const [restoreFile, setRestoreFile] = useState<File | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);

  if (!isSuperUser) {
    return (
      <>
        <Title title={translate("resources.backup.name")} />
        <Alert severity="warning" sx={{ mt: 2, maxWidth: 720 }}>
          {translate("resources.backup.accessDenied")}
        </Alert>
      </>
    );
  }

  const backupValid =
    backupPass.length >= MIN_PASSPHRASE && backupPass === backupPass2;

  const handleBackup = async () => {
    setDownloading(true);
    try {
      await downloadBackup(backupPass);
      notify("resources.backup.backup.started", { type: "info" });
    } catch (e) {
      notify(
        e instanceof Error ? e.message : translate("resources.backup.backup.error"),
        { type: "error" },
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleRestore = async () => {
    setConfirmOpen(false);
    if (!restoreFile) return;
    setRestoring(true);
    try {
      await restoreBackup(restoreFile, restorePass);
      notify("resources.backup.restore.ok", { type: "success" });
      setRestoreFile(null);
      setRestorePass("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      notify(
        e instanceof Error ? e.message : translate("resources.backup.restore.error"),
        { type: "error" },
      );
    } finally {
      setRestoring(false);
    }
  };

  const restoreValid = restoreFile != null && restorePass.length >= MIN_PASSPHRASE;

  return (
    <>
      <Title title={translate("resources.backup.name")} />

      <ScheduleCard />

      <Card sx={{ mt: 2, maxWidth: 720 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {translate("resources.backup.backup.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {translate("resources.backup.backup.hint")}
          </Typography>
          <Stack spacing={2} sx={{ maxWidth: 420 }}>
            <TextField
              type="password"
              label={translate("resources.backup.backup.passphrase")}
              value={backupPass}
              onChange={(e) => setBackupPass(e.target.value)}
              helperText={translate("resources.backup.backup.passphraseHint", {
                min: MIN_PASSPHRASE,
              })}
              autoComplete="new-password"
            />
            <TextField
              type="password"
              label={translate("resources.backup.backup.passphraseConfirm")}
              value={backupPass2}
              onChange={(e) => setBackupPass2(e.target.value)}
              error={backupPass2.length > 0 && backupPass !== backupPass2}
              helperText={
                backupPass2.length > 0 && backupPass !== backupPass2
                  ? translate("resources.backup.backup.passphraseMismatch")
                  : undefined
              }
              autoComplete="new-password"
            />
            <Box>
              <Button
                variant="contained"
                startIcon={<CloudDownloadIcon />}
                disabled={!backupValid || downloading}
                onClick={handleBackup}
              >
                {downloading
                  ? translate("resources.backup.backup.downloading")
                  : translate("resources.backup.backup.download")}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mt: 3, maxWidth: 720 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {translate("resources.backup.restore.title")}
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {translate("resources.backup.restore.warning")}
          </Alert>
          <Stack spacing={2} sx={{ maxWidth: 420 }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {translate("resources.backup.restore.file")}
              </Typography>
              <input
                ref={fileRef}
                type="file"
                accept=".rmtkbk"
                onChange={(e) => setRestoreFile(e.target.files?.[0] ?? null)}
              />
            </Box>
            <TextField
              type="password"
              label={translate("resources.backup.restore.passphrase")}
              value={restorePass}
              onChange={(e) => setRestorePass(e.target.value)}
              autoComplete="off"
            />
            <Box>
              <Button
                variant="contained"
                color="warning"
                startIcon={<RestoreIcon />}
                disabled={!restoreValid || restoring}
                onClick={() => setConfirmOpen(true)}
              >
                {restoring
                  ? translate("resources.backup.restore.restoring")
                  : translate("resources.backup.restore.restore")}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          {translate("resources.backup.restore.confirmTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {translate("resources.backup.restore.confirmBody")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>
            {translate("ra.action.cancel")}
          </Button>
          <Button color="warning" variant="contained" onClick={handleRestore}>
            {translate("resources.backup.restore.restore")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BackupPage;
